import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, testimonials, faqs } from '@/mocks/comics';

export default function AboutPage() {
  const [faqOpen, setFaqOpen] = useState<number | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const formUrl = 'https://readdy.ai/api/form/d9pdrp1n2trhb35gvdg0';

  const toggleFaq = (index: number) => {
    setFaqOpen(faqOpen === index ? null : index);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    const formData = new FormData(form);
    formData.delete('company_alt');
    try {
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请重试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://www.hanmanzhijia.com',
    description: siteInfo.description,
    foundingDate: siteInfo.founded,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteInfo.phone,
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: siteInfo.address,
      addressLocality: '北京',
      addressRegion: '北京',
      addressCountry: 'CN',
    },
    email: siteInfo.email,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <section className="py-10 md:py-14">
              <div className="text-center mb-12">
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
                  关于韩漫之家
                </h1>
                <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
                  {siteInfo.description}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                <div className="bg-background-100 rounded-xl p-8 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                    <i className="ri-shield-check-line text-xl" style={{ color: 'oklch(var(--primary-500))' }}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-900 mb-3">正版授权</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    平台所有漫画内容均获得韩国版权方正式授权，让每一位读者都能在合法合规的框架下享受优质的漫画阅读体验。
                  </p>
                </div>
                <div className="bg-background-100 rounded-xl p-8 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-flashlight-line text-xl" style={{ color: 'oklch(var(--accent-500))' }}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-900 mb-3">同步更新</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    与韩国版权方保持紧密合作，热门连载漫画实现与韩国几乎同步更新，让国内读者不再苦苦等待翻译进度。
                  </p>
                </div>
                <div className="bg-background-100 rounded-xl p-8 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className="ri-user-smile-line text-xl" style={{ color: 'oklch(var(--secondary-500))' }}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-900 mb-3">读者至上</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    始终以读者体验为核心，提供简洁流畅的阅读界面、智能的收藏功能以及个性化的漫画推荐服务。
                  </p>
                </div>
              </div>
            </section>

            <section className="py-10 md:py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
              <div className="max-w-[1400px] mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
                    品牌故事
                  </h2>
                  <p className="text-sm text-foreground-500">从零到千万读者的成长之路</p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <img
                      src="https://readdy.ai/api/search-image?query=modern%20cozy%20office%20with%20bookshelves%20filled%20with%20comic%20books%2C%20warm%20ambient%20lighting%2C%20creative%20workspace%20with%20digital%20tablets%20displaying%20webtoons%2C%20Korean%20style%20interior%2C%20clean%20and%20inviting%20atmosphere%2C%20soft%20natural%20light%20from%20windows%2C%20editorial%20photography%20style%2C%20harmonious%20composition&width=800&height=500&seq=about-brand-story&orientation=landscape"
                      alt="韩漫之家 品牌故事 办公环境"
                      className="w-full rounded-xl object-cover"
                      title="韩漫之家 - 品牌故事 - 海量正版漫画阅读平台"
                    />
                  </div>
                  <div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      韩漫之家成立于{siteInfo.founded}年，由一群热爱韩国漫画的年轻人创立。创始团队发现，当时国内读者想要看到高质量的韩国漫画非常困难——要么是盗版网站质量堪忧，要么是翻译速度跟不上更新节奏。于是，<strong>韩漫之家</strong>应运而生。
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      创业初期，团队只有五个人，挤在一个小小的办公室里。他们一个个联系韩国版权方，争取<strong>正版漫画</strong>资源。被拒绝了无数次，但从未放弃。终于，在坚持不懈的努力下，获得了第一批韩国漫画的正式授权。
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      从那以后，韩漫之家迅速发展壮大。如今，平台已收录超过{siteInfo.totalComics}部正版韩国漫画，拥有{siteInfo.totalUsers}注册用户，日活跃用户突破{siteInfo.dailyActive}。我们与数十家韩国顶级漫画出版社和创作平台建立了深度合作，成为国内读者首选的<strong>韩国漫画大全</strong>平台。
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      未来，韩漫之家将继续坚持正版路线，为读者提供更多优质的<strong>免费漫画</strong>和<strong>在线阅读</strong>服务，让每一位热爱漫画的读者都能在这里找到属于自己的精神世界。
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="py-10 md:py-14">
              <div className="max-w-[1400px] mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
                    读者口碑
                  </h2>
                  <p className="text-sm text-foreground-500">听听我们的读者怎么说</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {testimonials.map((t) => (
                    <div key={t.id} className="bg-background-100 rounded-xl p-6">
                      <div className="flex items-center gap-1 mb-3">
                        {Array.from({ length: 5 }, (_, i) => (
                          <i
                            key={i}
                            className={`text-sm ${i < t.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}
                          ></i>
                        ))}
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-xs font-medium text-primary-600">
                              {t.name.charAt(0)}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-foreground-800">{t.name}</span>
                        </div>
                        <span className="text-xs text-foreground-400">{t.date}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="py-10 md:py-14 bg-background-100 -mx-4 md:-mx-6 px-4 md:px-6">
              <div className="max-w-[1400px] mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
                    联系我们
                  </h2>
                  <p className="text-sm text-foreground-500">有任何问题或建议？我们随时倾听</p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                  <div>
                    <div className="space-y-5">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100">
                          <i className="ri-mail-line text-sm" style={{ color: 'oklch(var(--primary-500))' }}></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-heading font-bold text-foreground-900 mb-1">电子邮箱</h4>
                          <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-100">
                          <i className="ri-phone-line text-sm" style={{ color: 'oklch(var(--accent-500))' }}></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-heading font-bold text-foreground-900 mb-1">客服电话</h4>
                          <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                          <p className="text-xs text-foreground-400 mt-1">工作日 9:00 - 18:00</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100">
                          <i className="ri-map-pin-line text-sm" style={{ color: 'oklch(var(--secondary-500))' }}></i>
                        </div>
                        <div>
                          <h4 className="text-sm font-heading font-bold text-foreground-900 mb-1">公司地址</h4>
                          <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <form onSubmit={handleSubmit} data-readdy-form className="bg-background-50 rounded-xl p-6" action={formUrl} method="POST">
                      <div className="space-y-4">
                        <div>
                          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">姓名</label>
                          <input
                            id="contact-name"
                            name="name"
                            type="text"
                            required
                            className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
                            placeholder="请输入您的姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">邮箱</label>
                          <input
                            id="contact-email"
                            name="email"
                            type="email"
                            required
                            className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
                            placeholder="请输入您的邮箱"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">留言内容</label>
                          <textarea
                            id="contact-message"
                            name="message"
                            rows={4}
                            required
                            maxLength={500}
                            className="w-full px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 transition-colors resize-none"
                            placeholder="请输入您的留言（最多500字）"
                          ></textarea>
                        </div>
                        <input
                          type="text"
                          name="company_alt"
                          className="anti-spam-field"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                        <button
                          type="submit"
                          className="w-full px-5 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          发送留言
                        </button>
                      </div>
                      {formStatus === 'success' && (
                        <p className="mt-3 text-sm text-accent-600">留言已成功发送，我们会尽快回复您！</p>
                      )}
                      {formStatus === 'error' && (
                        <p className="mt-3 text-sm text-secondary-600">{formError}</p>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </section>

            <section className="py-10 md:py-14">
              <div className="max-w-[1400px] mx-auto">
                <div className="text-center mb-10">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-2">
                    常见问题
                  </h2>
                  <p className="text-sm text-foreground-500">快速解答您的疑问</p>
                </div>
                <div className="max-w-3xl mx-auto space-y-3">
                  {faqs.map((faq, index) => (
                    <div key={index} className="bg-background-100 rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleFaq(index)}
                        className="w-full flex items-center justify-between p-5 text-left cursor-pointer"
                      >
                        <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                        <i
                          className={`text-sm text-foreground-400 transition-transform flex-shrink-0 ${
                            faqOpen === index ? 'ri-subtract-line rotate-180' : 'ri-add-line'
                          }`}
                        ></i>
                      </button>
                      {faqOpen === index && (
                        <div className="px-5 pb-5">
                          <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}